import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Mic, MicOff, Camera, X, Loader2, FileText, Volume2, Square, Image as ImageIcon, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
const CHIPS = ["JF61 ka rate kya hai?", "Which chassis should I sell first?", "Sabse purana stock kaunsa hai?", "2 Freedom 125 ka estimate banao"];

const DRAFT_RE = /\[ESTIMATE_DRAFT\]\s*(\{[\s\S]*?\})\s*$/;
const parseDraft = (text) => {
  const m = text.match(DRAFT_RE);
  if (!m) return { clean: text, draft: null };
  try { return { clean: text.replace(DRAFT_RE, "").trim(), draft: JSON.parse(m[1]) }; }
  catch { return { clean: text.replace(DRAFT_RE, "").trim(), draft: null }; }
};
const toSpeech = (md) => md.replace(/\*\*/g, "").replace(/[#*•\-]/g, " ").replace(/₹/g, "rupees ").replace(/\s+/g, " ").trim();

const renderMd = (md) =>
  md.split("\n").map((line, i) => {
    const html = line.replace(/\*\*(.+?)\*\*/g, "<b class='text-slate-50'>$1</b>").replace(/^[-•*]\s+/, "• ").replace(/^#{1,4}\s+(.*)/, "<b class='text-indigo-300'>$1</b>");
    return <p key={i} className="min-h-[0.4rem]" dangerouslySetInnerHTML={{ __html: html }} />;
  });

const resizeImage = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const max = 1024;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  img.src = URL.createObjectURL(file);
});

export const AiChat = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const canTask = user?.role === "admin" || user?.screens?.includes("tasks");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const sessionId = useRef(sessionStorage.getItem("ai_session") || (() => { const s = `s${Date.now()}`; sessionStorage.setItem("ai_session", s); return s; })());
  const recRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [camMenu, setCamMenu] = useState(false);
  const bottomRef = useRef(null);
  const sendRef = useRef(null);
  const micRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      setOpen(true);
      const d = e.detail || {};
      if (d.message) setTimeout(() => sendRef.current?.(d.message), 300);
      else if (d.mode === "camera") setTimeout(() => setCamMenu(true), 400);
      else if (d.mode === "mic") setTimeout(() => micRef.current?.(), 400);
    };
    window.addEventListener("open-ai-chat", h);
    return () => window.removeEventListener("open-ai-chat", h);
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setMessages((m) => [...m, { role: "assistant", text: "Voice input is not supported in this browser. Please type instead." }]); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.lang = "hi-IN";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => setInput(Array.from(e.results).map((r) => r[0].transcript).join(""));
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  const speak = (idx, text) => {
    if (!window.speechSynthesis) return;
    if (speakingIdx === idx) { window.speechSynthesis.cancel(); setSpeakingIdx(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(toSpeech(text));
    u.lang = /[\u0900-\u097F]/.test(text) ? "hi-IN" : "en-IN";
    u.rate = 1.05;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    setSpeakingIdx(idx);
    window.speechSynthesis.speak(u);
  };

  const openDraft = (draft) => {
    sessionStorage.setItem("estimate_draft", JSON.stringify(draft));
    window.dispatchEvent(new CustomEvent("prefill-estimate"));
    window.speechSynthesis?.cancel();
    setOpen(false);
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (f) setImage(await resizeImage(f));
    e.target.value = "";
    setCamMenu(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg && !image) return;
    recRef.current?.stop();
    const img = image;
    const display = msg === "__GENERATE_INSIGHTS__" ? "✨ Generate Insights" : (msg || "📷 Photo");
    setMessages((m) => [...m, { role: "user", text: display, image: img }, { role: "assistant", text: "" }]);
    setInput(""); setImage(null); setBusy(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        body: JSON.stringify({ session_id: sessionId.current, message: msg, image_base64: img ? img.split(",")[1] : null }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + ev.delta }; return c; });
          if (ev.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: `⚠️ ${ev.error}` }; return c; });
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: "⚠️ Could not reach AI. Try again." }; return c; });
    } finally { setBusy(false); }
  };
  sendRef.current = send;
  micRef.current = toggleMic;

  return (
    <>
      <button data-testid="ai-chat-fab" onClick={() => setOpen(true)} title="Ask AI"
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-indigo-600 hover:bg-indigo-500 text-white rounded-l-xl shadow-lg px-2.5 py-4 flex flex-col items-center gap-1 transition-colors">
        <Sparkles className="h-5 w-5" />
        <span className="text-[9px] font-bold tracking-wider" style={{ writingMode: "vertical-rl" }}>ASK AI</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" data-testid="ai-chat-panel" className="w-full sm:max-w-md bg-[#111827] border-[#334155] text-slate-100 p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b border-[#334155]">
            <SheetTitle className="text-slate-100 flex items-center gap-2 text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Sparkles className="h-4 w-4 text-indigo-400" /> OPM Bajaj AI Assistant
            </SheetTitle>
            <p className="text-[11px] text-slate-400 text-left">Rates, FIFO picks & estimates — type, speak 🎤 or send a photo 📷 of the chassis plate / note.</p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <button data-testid="generate-insights-card" onClick={() => !busy && send("__GENERATE_INSIGHTS__")} disabled={busy}
              className="w-full text-left rounded-xl border border-violet-500/50 bg-gradient-to-r from-indigo-600/30 to-violet-600/30 hover:from-indigo-600/45 hover:to-violet-600/45 px-3.5 py-3 transition-colors disabled:opacity-50">
              <span className="flex items-center justify-between">
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-bold text-violet-200" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <Sparkles className="h-4 w-4 text-violet-300" /> Generate Insights
                  </span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Full business report — urgent sell, ageing, sub-dealers, RTO, trends</span>
                </span>
                <span className="shrink-0 rounded-full bg-violet-600 text-white text-[10px] font-bold px-3 py-1.5">Generate</span>
              </span>
            </button>
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Try asking:</p>
                {CHIPS.map((c) => (
                  <button key={c} data-testid="ai-chip" onClick={() => send(c)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 transition-colors">
                    {c}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => {
              const { clean, draft } = m.role === "assistant" ? parseDraft(m.text) : { clean: m.text, draft: null };
              const finished = m.role === "assistant" && clean && !(busy && i === messages.length - 1);
              return (
                <div key={i} className={`max-w-[88%] rounded-lg px-3 py-2 text-xs leading-relaxed ${m.role === "user" ? "ml-auto bg-blue-600 text-white" : "bg-[#1E293B] border border-[#334155] text-slate-300"}`}>
                  {m.image && <img src={m.image} alt="sent" className="rounded mb-1.5 max-h-36" />}
                  {m.role === "assistant" ? (clean ? renderMd(clean) : <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />) : m.text}
                  {draft?.chassis?.length > 0 && (
                    <button data-testid="ai-open-draft-btn" onClick={() => openDraft(draft)}
                      className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 text-xs font-semibold transition-colors">
                      <FileText className="h-3.5 w-3.5" /> Open in New Estimate ({draft.chassis.length} vehicle{draft.chassis.length > 1 ? "s" : ""})
                    </button>
                  )}
                  {finished && (
                    <div className="mt-1.5 flex items-center gap-3">
                      <button data-testid={`ai-speak-btn-${i}`} onClick={() => speak(i, clean)} title="Listen"
                        className={`flex items-center gap-1 text-[10px] ${speakingIdx === i ? "text-rose-400" : "text-indigo-400 hover:text-indigo-300"}`}>
                        {speakingIdx === i ? <><Square className="h-3 w-3" /> Stop</> : <><Volume2 className="h-3 w-3" /> Listen</>}
                      </button>
                      <a data-testid={`ai-wa-share-btn-${i}`} href={`https://wa.me/?text=${encodeURIComponent(clean.replace(/\*\*/g, "*").replace(/^#{1,4}\s+/gm, "").slice(0, 3200))}`}
                        target="_blank" rel="noreferrer" title="Share on WhatsApp"
                        className="flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300">
                        <Send className="h-3 w-3" /> WhatsApp
                      </a>
                      {canTask && (
                        <button data-testid={`ai-task-btn-${i}`} title="Create a task from this insight"
                          onClick={() => {
                            const txt = clean.replace(/\*\*/g, "").replace(/^#{1,4}\s+/gm, "").trim();
                            const firstLine = txt.split("\n").map((s) => s.trim()).find(Boolean) || "Follow-up from AI insight";
                            window.dispatchEvent(new CustomEvent("open-tasks-new", { detail: { headline: firstLine.slice(0, 70), description: txt.slice(0, 1500) } }));
                            setOpen(false);
                          }}
                          className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                          <ClipboardList className="h-3 w-3" /> Create Task
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-[#334155] p-3 space-y-2">
            {image && (
              <div className="relative inline-block">
                <img src={image} alt="preview" className="h-16 rounded border border-[#334155]" />
                <button onClick={() => setImage(null)} className="absolute -top-1.5 -right-1.5 bg-rose-600 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
              </div>
            )}
            <div className="relative flex items-center gap-1.5">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickImage} />
              {camMenu && (
                <div data-testid="ai-camera-menu" className="absolute bottom-12 left-0 z-50 w-48 rounded-xl border border-[#334155] bg-[#0B0F17] shadow-xl overflow-hidden">
                  <button data-testid="ai-camera-take" onClick={() => { setCamMenu(false); cameraRef.current?.click(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-3 text-sm text-slate-200 hover:bg-slate-800 text-left">
                    <Camera className="h-4 w-4 text-indigo-400" />Take Photo
                  </button>
                  <button data-testid="ai-camera-gallery" onClick={() => { setCamMenu(false); fileRef.current?.click(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-3 text-sm text-slate-200 hover:bg-slate-800 text-left border-t border-[#334155]">
                    <ImageIcon className="h-4 w-4 text-emerald-400" />Choose from Gallery
                  </button>
                </div>
              )}
              <Button data-testid="ai-camera-btn" size="sm" variant="ghost" onClick={() => setCamMenu((v) => !v)}
                className={`h-10 w-10 p-0 shrink-0 ${camMenu ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`} title="Photo / camera">
                <Camera className="h-5 w-5" />
              </Button>
              <Button data-testid="ai-mic-btn" size="sm" variant="ghost" onClick={toggleMic}
                className={`h-10 w-10 p-0 shrink-0 ${listening ? "bg-rose-600 text-white hover:bg-rose-500 animate-pulse" : "text-slate-400 hover:text-white hover:bg-slate-800"}`} title="Speak (Hindi/English)">
                {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
              <Input data-testid="ai-chat-input" placeholder={listening ? "Listening… boliye" : "JF61 ka rate? / photo bhejo…"} value={input}
                onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && send()}
                className="bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500 h-10 text-sm" />
              <Button data-testid="ai-send-btn" size="sm" onClick={() => send()} disabled={busy || (!input.trim() && !image)}
                className="h-10 w-10 p-0 bg-indigo-600 hover:bg-indigo-500 text-white shrink-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
